import {
  isAdultAddon,
  isBusinessAddon,
  isChildAddon,
  isFamilyAddon,
  isPetAddon,
  isTierOneAddon,
  isTierThreeAddon,
  isTierTwoAddon,
} from './addons';
import { locationCurrencyCodeMapper } from './currencies';
import { isUSMediumLocation } from './get-location-plans';
import { Location } from './countries';

export const addonsForCurrency = {
  GBP: {
    business: {
      tier1: {
        current: 'tier-1-v3-gbp',
      },
      tier2: {
        current: 'tier-2-v3-gbp',
      },
      tier3: {
        current: 'tier-3-v3-gbp',
      },
    },
    family: {
      adult: {
        current: 'adult-v3-gbp',
      },
      child: {
        current: 'child-v3-gbp',
      },
      pet: {
        current: 'pet-v3-gbp',
      },
    },
  },
  USD: {
    business: {
      tier1: {
        current: 'tier-1-v3-usd',
      },
      tier2: {
        current: 'tier-2-v3-usd',
      },
      tier3: {
        current: 'tier-2-v3-usd',
      },
    },
    family: {
      adult: {
        current: 'adult-v3-usd',
      },
      child: {
        current: 'child-v3-usd',
      },
      pet: {
        current: 'pet-v3-usd',
      },
    },
  },
  EUR: {
    business: {
      tier1: {
        current: 'tier-1-v3-eur',
      },
      tier2: {
        current: 'tier-2-v3-eur',
      },
      tier3: {
        current: 'tier-3-v3-eur',
      },
    },
    family: {
      adult: {
        current: 'adult-v3-eur',
      },
      child: {
        current: 'child-v3-eur',
      },
      pet: {
        current: 'pet-v3-eur',
      },
    },
  },
  AUD: {
    business: {
      tier1: {
        current: 'tier-1-v3-aud',
      },
      tier2: {
        current: 'tier-2-v3-aud',
      },
      tier3: {
        current: 'tier-3-v3-aud',
      },
    },
    family: {
      adult: {
        current: 'adult-v3-aud',
      },
      child: {
        current: 'child-v3-aud',
      },
      pet: {
        current: 'pet-v3-aud',
      },
    },
  },
  CAD: {
    business: {
      tier1: {
        current: 'tier-1-v3-cad',
      },
      tier2: {
        current: 'tier-2-v3-cad',
      },
      tier3: {
        current: 'tier-3-v3-cad',
      },
    },
    family: {
      adult: {
        current: 'adult-v3-cad',
      },
      child: {
        current: 'child-v3-cad',
      },
      pet: {
        current: 'pet-v3-cad',
      },
    },
  },
};

const addonsForUSMedium = {
  business: {
    tier1: {
      current: 'tier-1-v3-usd-medium',
    },
    tier2: {
      current: 'tier-2-v3-usd-medium',
    },
    tier3: {
      current: 'tier-2-v3-usd-medium',
    },
  },
  family: {
    adult: {
      current: 'adult-v3-usd-medium',
    },
    child: {
      current: 'child-v3-usd-medium',
    },
    pet: {
      current: 'pet-v3-usd-medium',
    },
  },
};

/**
 * Given the existing addon what is the corresponding addonId to select for the new currency ?
 * Note - we can't switch a user to a USMedium addon based solely on the new currency we would need to take their
 * location into account as well
 * @param {string} oldAddonId - The identifier of the existing addonId the user has
 * @param {Location} newLocation - The new location the user is changing their account over to
 * @return {string}
 */
export const getValidAddonIdFromOldAddonAndNewLocation = (oldAddonId: string, newLocation: Location) => {
  let newAddonId: string | null = null;
  const isTierOne = isTierOneAddon(oldAddonId);
  const isTierTwo = isTierTwoAddon(oldAddonId);
  const isTierThree = isTierThreeAddon(oldAddonId);
  const isAdult = isAdultAddon(oldAddonId);
  const isChild = isChildAddon(oldAddonId);
  const isPet = isPetAddon(oldAddonId);
  const newCurrencyCode = locationCurrencyCodeMapper[newLocation.countryCode];
  const canUseUSMedium = isUSMediumLocation(newLocation);

  if (isBusinessAddon(oldAddonId)) {
    if (isTierOne) {
      newAddonId = canUseUSMedium
        ? addonsForUSMedium.business.tier1.current
        : addonsForCurrency[newCurrencyCode].business.tier1.current;
    } else if (isTierTwo) {
      newAddonId = canUseUSMedium
        ? addonsForUSMedium.business.tier2.current
        : addonsForCurrency[newCurrencyCode].business.tier2.current;
    } else if (isTierThree) {
      newAddonId = canUseUSMedium
        ? addonsForUSMedium.business.tier3.current
        : addonsForCurrency[newCurrencyCode].business.tier3.current;
    }
  } else if (isFamilyAddon(oldAddonId)) {
    if (isAdult) {
      newAddonId = canUseUSMedium
        ? addonsForUSMedium.family.adult.current
        : addonsForCurrency[newCurrencyCode].family.adult.current;
    } else if (isChild) {
      newAddonId = canUseUSMedium
        ? addonsForUSMedium.family.child.current
        : addonsForCurrency[newCurrencyCode].family.child.current;
    } else if (isPet) {
      newAddonId = canUseUSMedium
        ? addonsForUSMedium.family.pet.current
        : addonsForCurrency[newCurrencyCode].family.pet.current;
    }
  }

  return newAddonId;
};
